import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import CAD from '@/assets/currency/cad.png'
import { TransactionType, useBuySellStore } from '@/reducers/transactionReducer'
import { StableCoins, stableCoinLogos } from '@/utils/currencies'

type SummaryRowProps = {
  startLabel?: string
  endLabel?: string
  useReferral?: boolean
}

export const SummaryRow = ({
  startLabel,
  endLabel,
  useReferral = false,
}: SummaryRowProps) => {
  const { t } = useTranslation()
  const transactionType = useBuySellStore.use.transactionType()

  const defaultStartLabel = startLabel || t('summaryRows.youPay')
  const defaultEndLabel = endLabel || t('summaryRows.youReceive')

  return (
    <div
      data-testid="container-summary-rows"
      className="grid grid-cols-4 md:grid-cols-[minmax(400px,1fr)_1fr_1fr_2fr] gap-y-3"
    >
      {transactionType === TransactionType.BUY && (
        <>
          <FiatRow
            data-testid="container-summary-fiat-row"
            isTop
            label={defaultStartLabel}
          />
          <CoinRow
            data-testid="container-summary-coin-row"
            label={defaultEndLabel}
            useReferral={useReferral}
          />
        </>
      )}
      {transactionType === TransactionType.SELL && (
        <>
          <CoinRow
            data-testid="container-summary-coin-row"
            isTop
            label={defaultStartLabel}
          />
          <FiatRow
            data-testid="container-summary-fiat-row"
            label={defaultEndLabel}
            useReferral={useReferral}
          />
        </>
      )}
    </div>
  )
}

type CoinRowProps = {
  label: string
  isTop?: boolean
  useReferral?: boolean
}

const topRowCellClass = 'pb-3'

export const FiatRow = ({
  label,
  isTop,
  useReferral = false,
}: CoinRowProps) => {
  const fiatAmount = useBuySellStore.use.fiatAmount()
  const adjustedFiatAmount = useBuySellStore.use.referralAdjustedFiatAmount()
  const difference =
    Math.round(adjustedFiatAmount * 100) / 100 -
    Math.round(fiatAmount * 100) / 100
  return (
    <>
      <div
        data-testid="text-summary-fiat-label"
        className={clsx({
          [topRowCellClass]: isTop,
        })}
      >
        {label}
      </div>
      <div
        data-testid="container-summary-fiat-icon"
        className={clsx('flex justify-center items-start', {
          [topRowCellClass]: isTop,
        })}
      >
        <img data-testid="img-summary-cad" src={CAD} height={25} width={25} />
      </div>
      {useReferral && (
        <div
          data-testid="text-summary-fiat-original"
          className={clsx({ [topRowCellClass]: isTop }, 'mr-4')}
        >
          <s style={{ color: 'grey' }}>
            {(Math.round(fiatAmount * 100) / 100).toFixed(2)}
          </s>
        </div>
      )}
      {!useReferral && (
        <div
          data-testid="text-summary-fiat-amount"
          className={clsx({ [topRowCellClass]: isTop }, 'mr-4')}
        >
          {(Math.round(fiatAmount * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && (
        <div
          data-testid="text-summary-fiat-currency-original"
          className={clsx({ [topRowCellClass]: isTop }, 'mr-4')}
        >
          <s style={{ color: 'grey' }}>CAD</s>
        </div>
      )}

      {!useReferral && (
        <div
          data-testid="text-summary-fiat-currency"
          className={clsx({ [topRowCellClass]: isTop }, 'mr-4')}
        >
          CAD
        </div>
      )}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && (
        <div data-testid="text-summary-fiat-difference">
          {difference > 0 ? '+' : ''}{' '}
          {(Math.round(difference * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && (
        <div data-testid="text-summary-fiat-adjusted">
          {(Math.round(adjustedFiatAmount * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && (
        <div
          data-testid="text-summary-fiat-currency-adjusted"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          CAD
        </div>
      )}
    </>
  )
}

export const CoinRow = ({
  label,
  isTop,
  useReferral = false,
}: CoinRowProps) => {
  const stableCoin = useBuySellStore.use.stableCoin()
  const stableCoinAmount = useBuySellStore.use.stableCoinAmount()
  const adjustedCoinAmount = useBuySellStore.use.referralAdjustedCoinAmount()
  const network = useBuySellStore.use.network()
  const difference =
    Math.round(adjustedCoinAmount * 100) / 100 -
    Math.round(stableCoinAmount * 100) / 100
  return (
    <>
      <div
        data-testid="text-summary-coin-label"
        className={clsx({ [topRowCellClass]: isTop })}
      >
        {label}
      </div>
      <div
        data-testid="container-summary-coin-icon"
        className={clsx('flex justify-center items-start', {
          [topRowCellClass]: isTop,
        })}
      >
        <img
          data-testid="img-summary-coin"
          src={stableCoinLogos[`${stableCoin}${network}`]}
          height={25}
          width={25}
        />
      </div>
      {useReferral && (
        <div
          data-testid="text-summary-coin-original"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          <s style={{ color: 'grey' }}>
            {(Math.round(stableCoinAmount * 100) / 100).toFixed(2)}
          </s>
        </div>
      )}
      {!useReferral && (
        <div
          data-testid="text-summary-coin-amount"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          {(Math.round(stableCoinAmount * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && (
        <div
          data-testid="text-summary-coin-currency-original"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          <s style={{ color: 'grey' }}>
            {StableCoins[stableCoin]}-{network}
          </s>
        </div>
      )}

      {!useReferral && (
        <div
          data-testid="text-summary-coin-currency"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          {StableCoins[stableCoin]}-{network}
        </div>
      )}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && (
        <div data-testid="text-summary-coin-difference">
          {difference > 0 ? '+' : ''}{' '}
          {(Math.round(difference * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && <div></div>}

      {useReferral && <div></div>}

      {useReferral && (
        <div data-testid="text-summary-coin-adjusted">
          {(Math.round(adjustedCoinAmount * 100) / 100).toFixed(2)}
        </div>
      )}

      {useReferral && (
        <div
          data-testid="text-summary-coin-currency-adjusted"
          className={clsx({ [topRowCellClass]: isTop })}
        >
          {StableCoins[stableCoin]}-{network}
        </div>
      )}
    </>
  )
}
